export type Locale = "de" | "en";

export type RouteKey =
  | "home"
  | "services"
  | "about"
  | "caseStudies"
  | "contact"
  | "consultation"
  | "website"
  | "booking"
  | "phone"
  | "privacy"
  | "imprint"
  | "terms"
  | "blog"
  | "whoItIsFor";

export interface RouteEntry {
  key: RouteKey;
  de: string;
  en: string;
}

/**
 * Single source of truth for the bilingual route map.
 * Used by App.tsx, SEOHead, LanguageSwitcher, Header, Footer, sitemap, JSON-LD.
 */
export const ROUTES: readonly RouteEntry[] = [
  { key: "home",         de: "/de",                       en: "/en" },
  { key: "services",     de: "/de/leistungen",            en: "/en/services" },
  { key: "about",        de: "/de/ueber-uns",             en: "/en/about" },
  { key: "caseStudies",  de: "/de/praxisbeispiele",       en: "/en/case-studies" },
  { key: "contact",      de: "/de/kontakt",               en: "/en/contact" },
  { key: "consultation", de: "/de/erstgespraech",         en: "/en/consultation" },
  { key: "website",      de: "/de/website-fuer-praxen",   en: "/en/medical-website" },
  { key: "booking",      de: "/de/terminbuchung-praxis",  en: "/en/appointment-booking" },
  { key: "phone",        de: "/de/telefonservice-praxis", en: "/en/phone-service" },
  { key: "privacy",      de: "/de/datenschutz",           en: "/en/privacy" },
  { key: "imprint",      de: "/de/impressum",             en: "/en/imprint" },
  { key: "terms",        de: "/de/agb",                   en: "/en/terms" },
  { key: "blog",         de: "/de/blog",                  en: "/en/blog" },
  { key: "whoItIsFor",   de: "/de/fuer-wen",              en: "/en/who-it-is-for" },
] as const;

const stripTrailingSlash = (p: string): string =>
  p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;

/** Locale derived from URL prefix. Returns null for non-localized paths (e.g. /admin, /blog). */
export function getLocaleFromPath(pathname: string): Locale | null {
  if (pathname === "/de" || pathname.startsWith("/de/")) return "de";
  if (pathname === "/en" || pathname.startsWith("/en/")) return "en";
  return null;
}

/** Find which RouteEntry a pathname belongs to (in either locale). */
export function getRouteEntry(pathname: string): RouteEntry | null {
  const p = stripTrailingSlash(pathname);
  return ROUTES.find((r) => r.de === p || r.en === p) ?? null;
}

export function getRouteKey(pathname: string): RouteKey | null {
  return getRouteEntry(pathname)?.key ?? null;
}

/**
 * Counterpart URL in the other locale. Returns null if pathname isn't a known
 * localized route.
 *
 * For blog posts, callers SHOULD pass `blogSlugMap` so the counterpart points
 * directly at the localized slug (e.g. /de/blog/{de-slug} → /en/blog/{en-slug}).
 * Without the map we fall back to swapping only the locale prefix, which then
 * triggers an in-component redirect on the BlogPost page.
 */
export function getCounterpart(
  pathname: string,
  targetLocale: Locale,
  blogSlugMap?: { de: string; en?: string },
): string | null {
  const blogPostMatch = pathname.match(/^\/(de|en)\/blog\/(.+)$/);
  if (blogPostMatch) {
    if (blogSlugMap) {
      const slug =
        targetLocale === "en"
          ? blogSlugMap.en ?? blogSlugMap.de
          : blogSlugMap.de;
      return `/${targetLocale}/blog/${slug}`;
    }
    const slug = blogPostMatch[2];
    return `/${targetLocale}/blog/${slug}`;
  }
  const entry = getRouteEntry(pathname);
  return entry ? entry[targetLocale] : null;
}

export function getLocalizedPath(key: RouteKey, locale: Locale): string {
  const entry = ROUTES.find((r) => r.key === key);
  if (!entry) throw new Error(`Unknown route key: ${key}`);
  return entry[locale];
}

/**
 * Localized URL for a single blog post. Accepts either the full slug object
 * `{ de, en }` or a raw string (treated as already locale-correct). When the
 * EN translation is missing, falls back to the DE slug — callers that need
 * to hide untranslated posts should do so at the data layer.
 */
export function getLocalizedBlogPostPath(
  slug: string | { de: string; en?: string },
  locale: Locale,
): string {
  if (typeof slug === "string") return `/${locale}/blog/${slug}`;
  const value = locale === "en" ? slug.en ?? slug.de : slug.de;
  return `/${locale}/blog/${value}`;
}

/**
 * Legacy root-level routes redirect to localized equivalents.
 * German source paths → /de/*, historical English aliases → /en/*.
 */
export const LEGACY_REDIRECTS: ReadonlyArray<{ from: string; to: string }> = [
  // Root → DE home
  { from: "/",                       to: "/de" },

  // Old German root paths → /de/*
  { from: "/leistungen",             to: "/de/leistungen" },
  { from: "/ueber-uns",              to: "/de/ueber-uns" },
  { from: "/praxisbeispiele",        to: "/de/praxisbeispiele" },
  { from: "/kontakt",                to: "/de/kontakt" },
  { from: "/erstgespraech",          to: "/de/erstgespraech" },
  { from: "/website-fuer-praxen",    to: "/de/website-fuer-praxen" },
  { from: "/terminbuchung-praxis",   to: "/de/terminbuchung-praxis" },
  { from: "/telefonservice-praxis",  to: "/de/telefonservice-praxis" },
  { from: "/datenschutz",            to: "/de/datenschutz" },
  { from: "/impressum",              to: "/de/impressum" },
  { from: "/agb",                    to: "/de/agb" },
  { from: "/blog",                   to: "/de/blog" },

  // Care preview — unprefixed legacy URL → DE preview
  { from: "/preview/care",           to: "/de/preview/care" },

  // Flow preview — unprefixed legacy URL → DE preview
  { from: "/preview/flow",           to: "/de/preview/flow" },

  // Start preview — unprefixed/legacy URLs → DE preview
  { from: "/preview/start",          to: "/de/preview/start" },

  // Foundation preview (cardio-nova) — unprefixed legacy URL → DE preview
  { from: "/preview/foundation",     to: "/de/preview/foundation" },

  // Historical English aliases → /en/*
  { from: "/about",                  to: "/en/about" },
  { from: "/services",               to: "/en/services" },
  { from: "/case-studies",           to: "/en/case-studies" },
  { from: "/contact",                to: "/en/contact" },
  { from: "/consultation",           to: "/en/consultation" },
  { from: "/privacy",                to: "/en/privacy" },
  { from: "/imprint",                to: "/en/imprint" },
  { from: "/terms",                  to: "/en/terms" },
];

export const SITE_URL = "https://onepractix.com";

export function absoluteUrl(path: string): string {
  return `${SITE_URL}${path}`;
}